/**
 * Blog dynamic element markers.
 *
 * Current syntax: <!-- wstawka N --> pulls the Nth entry of the article's
 * custom.blog_embeds metafield (product / collection / gallery / lookbook).
 *
 * Legacy syntax: <!-- produkty -->, <!-- kolekcje -->, <!-- galeria --> render
 * the single old per-type metafield. Kept working for existing articles.
 *
 * Handles initial load + theme editor section re-renders.
 */
window.hdtBlogMarkers = (function () {
  'use strict';

  const LEGACY_MARKERS = [
    { text: 'produkty', key: 'prod', templateId: 'hdt-blog-prod-', token: /PROD/g },
    { text: 'kolekcje', key: 'coll', templateId: 'hdt-blog-coll-', token: /COLL/g },
    { text: 'galeria', key: 'gal', templateId: 'hdt-blog-gallery-', token: /GAL/g }
  ];
  const EMBED_MARKER = /^wstawka\s+(\d+)$/;
  const EMBED_TOKEN = /EMB/g;
  const SENTINEL = '__N__';

  /**
   * Makes ids and their references unique per marker occurrence.
   *
   * Ids are read and written through attributes on purpose: on a <form> the `id`
   * property is shadowed by a control named "id", and every product card with a
   * quick-add button ships <input name="id"> — reading `el.id` there returns that
   * input, not a string.
   */
  function rewriteTokens(fragment, token, uid) {
    for (const el of fragment.querySelectorAll('*')) {
      const id = el.getAttribute('id');
      if (id) el.setAttribute('id', id.replace(token, uid));
      for (const attr of ['aria-controls', 'for', 'controls']) {
        const value = el.getAttribute(attr);
        if (value) el.setAttribute(attr, value.replace(token, uid));
      }
    }
    for (const style of fragment.querySelectorAll('style')) {
      if (style.textContent) style.textContent = style.textContent.replace(token, uid);
    }
  }

  /** A marker alone in a paragraph should replace the paragraph, not sit inside it. */
  function replacementTarget(comment) {
    const parent = comment.parentNode;
    if (parent && parent.tagName === 'P' && parent.childNodes.length === 1) return parent;
    return comment;
  }

  function missingEmbedNode(content, number) {
    const hint = document.createElement('div');
    hint.className = 'hdt-blog-embed__missing';
    const text = content.getAttribute('data-embed-missing-text') || '';
    hint.textContent = text ? text.replace(SENTINEL, number) : number;
    return hint;
  }

  /** Clones a template in place of the marker, with ids made unique. */
  function replaceMarker(comment, template, token, uid) {
    const fragment = template.content.cloneNode(true);
    rewriteTokens(fragment, token, uid);
    const target = replacementTarget(comment);
    target.parentNode.replaceChild(fragment, target);
  }

  /**
   * The lightbox binds its click handler to the <img>, which is not focusable.
   * The tiles are buttons, so forward keyboard activation to the image.
   */
  function enableGalleryKeyboard(root) {
    for (const tile of root.querySelectorAll('button.hdt-blog-gallery__media')) {
      tile.addEventListener('click', (event) => {
        // A real pointer click already reached the image itself.
        if (event.detail !== 0) return;
        tile.querySelector('img')?.click();
      });
    }
  }

  function init(blockId) {
    const content = document.getElementById('hdt-artc-content-' + blockId);
    if (!content) return;

    const legacy = [];
    for (const marker of LEGACY_MARKERS) {
      const template = document.getElementById(marker.templateId + blockId);
      if (template) legacy.push({ ...marker, template });
    }

    const hasEmbeds = !!document.getElementById('hdt-blog-embed-' + blockId + '-1');
    if (legacy.length === 0 && !hasEmbeds) return;

    // Avoid double-processing
    if (content.getAttribute('data-markers-initialized') === 'true') return;
    content.setAttribute('data-markers-initialized', 'true');

    const walker = document.createTreeWalker(content, NodeFilter.SHOW_COMMENT, null, false);
    const comments = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) comments.push(node);

    const inDesignMode = !!(window.Shopify && window.Shopify.designMode);
    const counts = {};

    for (const comment of comments) {
      const text = comment.nodeValue ? comment.nodeValue.trim().toLowerCase() : '';
      const embedMatch = text.match(EMBED_MARKER);

      if (embedMatch) {
        const number = embedMatch[1];
        const template = document.getElementById('hdt-blog-embed-' + blockId + '-' + number);

        if (!template) {
          // Unknown number: invisible on the storefront, flagged in the editor.
          if (inDesignMode) {
            const target = replacementTarget(comment);
            target.parentNode.replaceChild(missingEmbedNode(content, number), target);
          }
          continue;
        }

        const key = 'emb' + number;
        counts[key] = (counts[key] || 0) + 1;
        replaceMarker(comment, template, EMBED_TOKEN, key + 'x' + counts[key]);
        continue;
      }

      const marker = legacy.find((item) => item.text === text);
      if (!marker) continue;

      counts[marker.key] = (counts[marker.key] || 0) + 1;
      replaceMarker(comment, marker.template, marker.token, marker.key + counts[marker.key]);
    }

    enableGalleryKeyboard(content);
  }

  function initAll() {
    for (const el of document.querySelectorAll('[data-blog-block-id]')) {
      init(el.getAttribute('data-blog-block-id'));
    }
  }

  function initSection(sectionEl) {
    const contentEl = sectionEl.querySelector('[data-blog-block-id]');
    if (contentEl) init(contentEl.getAttribute('data-blog-block-id'));
  }

  // Run immediately (DOM is already parsed at this point since script loads at bottom)
  initAll();

  // Handle theme editor section re-renders
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => initSection(e.target));
  }

  return { init: init, initAll: initAll, initSection: initSection };
})();
